import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, fmtMoney } from './UI';

// RentalMan invoices on a customer's account(s), from the All Invoices feed
// (GET /api/customers/invoices). George 2026-10-06: reps see every paid and
// unpaid invoice and filter by month, paid, unpaid, unpaid over 90 days.
// Age is from the invoice date: the feed carries no due date.
const STATUS_FILTERS = [
  { value: 'all', label: 'All invoices' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'over90', label: 'Unpaid over 90 days' },
];

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

const fmtDay = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${m}/${d}/${y}`;
};

export default function CustomerInvoices({ accounts }) {
  const accountNos = accounts.map(a => a.account_no).filter(Boolean);
  const key = accountNos.join(',');
  const [account, setAccount] = useState('');        // '' = every matched account
  const [status, setStatus] = useState('all');
  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { setAccount(''); setMonth(''); }, [key]);

  useEffect(() => {
    if (!key) return;
    setError('');
    const params = { accounts: account || key, status };
    if (month) params.month = month;
    // A slower earlier response must not land over the filter clicked last.
    let current = true;
    api.customerInvoices(params)
      .then(d => { if (current) setData(d); })
      .catch(e => { if (current) setError(e.message); });
    return () => { current = false; };
  }, [key, account, status, month]);

  if (!accountNos.length) return null;

  const s = data?.summary;
  const multi = accountNos.length > 1;

  return (
    <div style={styles.card}>
      <h2 style={styles.h2}>
        RentalMan Invoices{' '}
        <span style={{ color: '#6B7280', fontWeight: 400 }}>
          · Acct {account || accountNos.join(', ')}
        </span>
      </h2>

      {error && <div style={styles.errorBox}>{error}</div>}

      {s && (
        <div style={styles.kpiGrid}>
          <Tile label="Paid" value={fmtMoney(s.paid_total)} sub={`${s.paid_count} invoices`}
            active={status === 'paid'} onClick={() => setStatus('paid')} />
          <Tile label="Unpaid" value={fmtMoney(s.unpaid_total)} sub={`${s.unpaid_count} invoices`}
            active={status === 'unpaid'} onClick={() => setStatus('unpaid')} />
          <Tile label="Unpaid over 90 days" value={fmtMoney(s.over90_total)} sub={`${s.over90_count} invoices · by invoice date`}
            active={status === 'over90'} onClick={() => setStatus('over90')} warn={Number(s.over90_total) > 0} />
          <Tile label="All invoices" value={s.count} sub="every status, all time"
            active={status === 'all'} onClick={() => setStatus('all')} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {multi && (
          <select style={{ ...styles.input, width: 200 }} value={account} onChange={e => setAccount(e.target.value)}>
            <option value="">All {accountNos.length} accounts</option>
            {accounts.map(a => (
              <option key={a.account_no} value={a.account_no}>
                Acct {a.account_no}{a.location_code ? ` · Loc ${a.location_code}` : ''}
              </option>
            ))}
          </select>
        )}
        <select style={{ ...styles.input, width: 210 }} value={status} onChange={e => setStatus(e.target.value)}>
          {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select style={{ ...styles.input, width: 170 }} value={month} onChange={e => setMonth(e.target.value)}>
          <option value="">All months</option>
          {(data?.months || []).map(m => (
            <option key={m.month} value={m.month}>{monthLabel(m.month)} ({m.count})</option>
          ))}
        </select>
        {(status !== 'all' || month || account) && (
          <button style={styles.btnSecondary} onClick={() => { setStatus('all'); setMonth(''); setAccount(''); }}>Reset</button>
        )}
      </div>

      {data && (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Invoice #</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Status</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Days Open</th>
                {multi && <th style={styles.th}>Acct</th>}
                <th style={styles.th}>Branch</th>
                <th style={styles.th}>Job #</th>
                <th style={styles.th}>PO #</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Ex-Tax</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.length === 0 && (
                <tr><td colSpan={multi ? 10 : 9} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                  No invoices match these filters.
                </td></tr>
              )}
              {data.invoices.map(i => (
                <tr key={`${i.account_no}-${i.invoice_no}-${i.invoice_seq}`}>
                  <td style={styles.td}>{i.invoice_no}-{i.invoice_seq}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{fmtDay(i.invoice_date)}</td>
                  <td style={styles.td}><PaidPill status={i.invoice_status} days={i.days_open} /></td>
                  <td style={{ ...styles.td, textAlign: 'right', color: i.days_open > 90 ? '#B91C1C' : undefined, fontWeight: i.days_open > 90 ? 700 : undefined }}>
                    {i.days_open == null ? '' : i.days_open}
                  </td>
                  {multi && <td style={styles.td}>{i.account_no}</td>}
                  <td style={styles.td}>{i.branch_location || ''}</td>
                  <td style={styles.td}>{i.job_no || ''}</td>
                  <td style={styles.td}>{i.po_number || ''}</td>
                  <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(i.amount_total)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap', color: '#6B7280' }}>{fmtMoney(i.amount_ex_tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.truncated && (
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>
              Showing the newest 1,000. Pick a month or status to narrow it.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PaidPill({ status, days }) {
  const paid = status === 'PD';
  const late = !paid && days > 90;
  const look = paid
    ? { bg: '#D1FAE5', fg: '#065F46', label: 'Paid' }
    : late
    ? { bg: '#FEE2E2', fg: '#991B1B', label: 'Unpaid 90+' }
    : { bg: '#FEF3C7', fg: '#92400E', label: 'Unpaid' };
  return (
    <span title={`RentalMan status ${status || 'blank'}`} style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 10,
      background: look.bg, color: look.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {look.label}
    </span>
  );
}

const Tile = ({ label, value, sub, active, onClick, warn }) => (
  <button type="button" onClick={onClick} style={{
    ...styles.kpiCard, textAlign: 'left', cursor: 'pointer', font: 'inherit',
    border: active ? '2px solid #EF6622' : styles.kpiCard.border,
  }}>
    <div style={styles.kpiLabel}>{label}</div>
    <div style={{ ...styles.kpiValue, color: warn ? '#B91C1C' : styles.kpiValue.color }}>{value}</div>
    {sub && <div style={styles.kpiSubtext}>{sub}</div>}
  </button>
);
